import React, { useState } from 'react';
import { Send, Trash2, ShieldCheck, Loader2 } from 'lucide-react';
import type { TransparencyComment, TransparencyCommentType } from '@yawi/types';
import { useServices, useToast, useDeleteConfirmation } from '@/app/providers';
import { useDateFormatter } from '@/shared/hooks';
import { useAbility } from '@/modules/identity/application/ability';

interface ProjectCommentsProps {
  projectId: string;
  comments: TransparencyComment[];
  commentsEnabled: boolean;
  questionsEnabled: boolean;
  onChanged: () => void;
}

export const ProjectComments: React.FC<ProjectCommentsProps> = ({
  projectId,
  comments,
  commentsEnabled,
  questionsEnabled,
  onChanged,
}) => {
  const { formatDateTime } = useDateFormatter();
  const { transparencyProjectRepo, currentUser } = useServices();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const ability = useAbility();
  const canModerate = ability.can('manage', 'all') || ability.can('delete', 'TransparencyComment');

  const [tab, setTab] = useState<TransparencyCommentType>('COMMENT');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const tabEnabled = tab === 'COMMENT' ? commentsEnabled : questionsEnabled;
  const filtered = comments.filter((c) => c.type === tab);

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await transparencyProjectRepo.addComment(projectId, { type: tab, text: text.trim() });
      setText('');
      onChanged();
    } catch {
      toast.error(tab === 'COMMENT' ? 'Error al comentar' : 'Error al enviar la pregunta');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (comment: TransparencyComment) => {
    const canDeleteThis = canModerate || comment.userId === currentUser?.id;
    if (!canDeleteThis) return;
    await confirmDelete({
      itemName: comment.text.slice(0, 40),
      itemType: tab === 'COMMENT' ? 'Comentario' : 'Pregunta',
      onConfirm: async () => {
        await transparencyProjectRepo.deleteComment(projectId, comment.id);
        onChanged();
      },
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button
          onClick={() => setTab('COMMENT')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            tab === 'COMMENT' ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600'
          }`}
        >
          Comentarios ({comments.filter((c) => c.type === 'COMMENT').length})
        </button>
        <button
          onClick={() => setTab('QUESTION')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            tab === 'QUESTION' ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600'
          }`}
        >
          Preguntas ({comments.filter((c) => c.type === 'QUESTION').length})
        </button>
      </div>

      {!tabEnabled ? (
        <p className="text-sm text-slate-400">
          {tab === 'COMMENT' ? 'Los comentarios están deshabilitados para este proyecto.' : 'Las preguntas están deshabilitadas para este proyecto.'}
        </p>
      ) : (
        <>
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-400">
              {tab === 'COMMENT' ? 'Aún no hay comentarios.' : 'Aún no hay preguntas.'}
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
              {filtered.map((c) => (
                <div key={c.id} className="flex items-start justify-between gap-2 p-3 rounded-lg bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-slate-800">{c.userName}</span>
                      {c.isAdmin && <ShieldCheck size={12} className="text-amber-500" />}
                      <span className="text-xs text-slate-400">{formatDateTime(c.createdAt)}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5 break-words">{c.text}</p>
                  </div>
                  {(canModerate || c.userId === currentUser?.id) && (
                    <button
                      onClick={() => handleDelete(c)}
                      className="text-slate-300 hover:text-red-500 transition-colors shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={tab === 'COMMENT' ? 'Escribe un comentario...' : 'Escribe tu pregunta...'}
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
            <button
              onClick={handleSend}
              disabled={sending || !text.trim()}
              className="px-3 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
